import { useEffect } from "react";
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { SignIn, SignUp } from "@clerk/react";
import { Toaster } from "@/components/ui/sonner";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import RfqDrawer from "@/components/site/RfqDrawer";
import { CookieBanner } from "@/components/site/common";
import { AppProvider } from "@/lib/store";
import Home from "@/pages/Home";
import { Achievements, Legacy, Team } from "@/pages/About";
import { AllProducts, Category, ProductsLanding } from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import Enquiry from "@/pages/Enquiry";
import Calculator from "@/pages/Calculator";
import Careers from "@/pages/Careers";
import Media, { MediaArticle } from "@/pages/Media";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import Admin from "@/pages/admin/Admin";

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function SiteLayout() {
  return (
    <>
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-3 focus:bg-[var(--paper)]">Skip to content</a>
      <Header />
      <main id="main"><Outlet /></main>
      <Footer />
      <RfqDrawer />
      <CookieBanner />
    </>
  );
}

const AuthLayout = () => <ClerkAuthProvider><Outlet /></ClerkAuthProvider>;
const AuthPage = ({ children }) => <div className="min-h-screen flex items-center justify-center p-6 bg-[var(--ivory)]">{children}</div>;

function NotFound() {
  return <div data-testid="not-found" className="wrap py-32"><p className="eyebrow">404</p><h1 className="text-5xl mt-3">Page not found</h1><a href="/" className="link mt-6 inline-block">Return home</a></div>;
}

export default function App() {
  return (
    <AppProvider>
      <BrowserRouter>
        <ScrollTop />
        <Routes>
          <Route element={<SiteLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<Navigate to="/about/legacy" replace />} />
            <Route path="/about/legacy" element={<Legacy />} />
            <Route path="/about/team" element={<Team />} />
            <Route path="/about/achievements" element={<Achievements />} />
            <Route path="/products" element={<ProductsLanding />} />
            <Route path="/products/all" element={<AllProducts />} />
            <Route path="/products/:category" element={<Category />} />
            <Route path="/products/:category/:slug" element={<ProductDetail />} />
            <Route path="/enquiry" element={<Enquiry />} />
            <Route path="/shipment-calculator" element={<Calculator />} />
            <Route path="/careers" element={<Careers />} />
            <Route path="/media" element={<Media />} />
            <Route path="/media/:slug" element={<MediaArticle />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/contacts" element={<Navigate to="/contact" replace />} />
            <Route path="/privacy" element={<Legal doc="privacy" />} />
            <Route path="/terms" element={<Legal doc="terms" />} />
            <Route path="*" element={<NotFound />} />
          </Route>
          <Route element={<AuthLayout />}>
            <Route path="/admin" element={<Admin />} />
            <Route path="/sign-in/*" element={<AuthPage><SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" withSignUp fallbackRedirectUrl="/admin" /></AuthPage>} />
            <Route path="/sign-up/*" element={<AuthPage><SignUp routing="path" path="/sign-up" signInUrl="/sign-in" /></AuthPage>} />
          </Route>
        </Routes>
        <Toaster position="bottom-right" />
      </BrowserRouter>
    </AppProvider>
  );
}
